import RequestMethod from "../models/client/RequestMethod";
import RestResponse from "../models/client/RestResponse";
import IdRequest from "../models/client/requests/IdRequest";
import Message, { MessageRating } from "../models/conversation/Message";
import DocumentReference from "../models/conversation/document";
import { SearchOption } from "@/models/base/SearchOption";
import { RestClient } from "./restClient";

export type RateMessageRequest = {
  conversationId: string;
  rating: MessageRating;
} & IdRequest;

export type SendMessageRequest = {
  sessionId: string;
  text: string;
  conversationId: string;
  language: string;
  chainOfThought: boolean;
};

export type MemoryReference = {
  documentIndex: string;
  documentId: string;
  chunkId: string;
  title: string;
  documentTitle: string;
  documentType: string;
  isActive: boolean;
  source: string;
  referenceId: number;
};

export type MemoryMessage = {
  actor: string;
  text: string;
  references: MemoryReference[];
};

export type PrepareStreamRequest = {
  text: string;
  language: string;
  chainOfThought: boolean;
};

export type PrepareStreamResponse = {
  conversationId: string;
  tenantId: string;
  userId: string;
  prompt: string;
  language?: string;
  chainOfThought: boolean;
  generateTitle: boolean;
  toneOfVoice: string;
  searchOptions: SearchOption[];
  documentIds: string[];
  documentWorkspaceMap: Record<string, string>;
  memory: MemoryMessage[];
};

export type SaveCompletedMessageRequest = {
  id?: string;
  human: boolean;
  text: string;
  isClosed?: boolean;
  references: DocumentReference[];
  language?: string;
  chainOfThought?: boolean;
  chainOfThoughtMessage?: string;
  revisedPrompt?: string;
  lemmatizedText?: Record<string, string>;
  statusShort?: string;
  statusLong?: string;
  conversationId: string;
};

export type SendMessageContextFilter = {
  workspaceId: string;
  documentIds?: string[];
  documentIdsWithRevisionNumbers?: DocumentIdWithRevisionNumber[];
};

export type WorkspaceContext = Record<string, DocumentIdWithRevisionNumber[]>;
export type DocumentIdWithRevisionNumber = {
  documentId: string;
  activeRevisionNumber?: number;
};

export type GetMessagesByConversationIdRequest = {
  conversationId: string;
};

export type DeleteMessageRequest = {
  conversationId: string;
  id: string;
};

export type MessageServiceType = {
  rateMessage: (request: RateMessageRequest) => RestResponse<Message>;
  queryByConversationId: (
    request: GetMessagesByConversationIdRequest
  ) => RestResponse<Message[]>;
  sendMessage: (request: SendMessageRequest) => RestResponse<Message>;
  prepareStream: (
    request: PrepareStreamRequest & { conversationId: string }
  ) => RestResponse<PrepareStreamResponse>;
  saveCompletedMessage: (
    request: SaveCompletedMessageRequest
  ) => RestResponse<Message>;
  deleteMessage: (request: DeleteMessageRequest) => RestResponse<void>;
};

const messageServices = (
  client: RestClient,
  path: string
): MessageServiceType => ({
  rateMessage: async (request: RateMessageRequest) =>
    await client.fetch<Message>(
      RequestMethod.PUT,
      `${path}/${request.conversationId}/message/${request.id}/rate`,
      request
    ),
  queryByConversationId: async (request: GetMessagesByConversationIdRequest) =>
    await client.fetch<Message[]>(
      RequestMethod.GET,
      `${path}/${request.conversationId}/message/all`
    ),
  sendMessage: async (request: SendMessageRequest) =>
    await client.fetch<Message>(
      RequestMethod.POST,
      `${path}/${request.conversationId}/message/sendMessage`,
      request
    ),
  prepareStream: async (
    request: PrepareStreamRequest & { conversationId: string }
  ) =>
    await client.fetch<PrepareStreamResponse>(
      RequestMethod.POST,
      `${path}/${request.conversationId}/message/prepareStream`,
      {
        text: request.text,
        language: request.language,
        chainOfThought: request.chainOfThought,
      }
    ),
  saveCompletedMessage: async (request: SaveCompletedMessageRequest) =>
    await client.fetch<Message>(
      RequestMethod.POST,
      `${path}/${request.conversationId}/message/complete`,
      {
        id: request.id,
        human: request.human,
        text: request.text,
        isClosed: request.isClosed ?? true,
        references: request.references,
        language: request.language,
        chainOfThought: request.chainOfThought,
        chainOfThoughtMessage: request.chainOfThoughtMessage,
        revisedPrompt: request.revisedPrompt,
        lemmatizedText: request.lemmatizedText,
        statusShort: request.statusShort,
        statusLong: request.statusLong,
      }
    ),
  deleteMessage: async (request: DeleteMessageRequest) =>
    await client.fetch<void>(
      RequestMethod.DELETE,
      `${path}/${request.conversationId}/message/${request.id}`
    ),
});

export default messageServices;
